import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Camera, Save, Upload as UploadIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { uploadImage } from '@/lib/upload';
import { ImageZoom } from '@/components/ImageZoom';

const ProfileSetup = () => {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: '', nickname: '', phone: '', pincode: '', town: '', profile_picture: '', bio: '',
    social_links: { youtube: '', instagram: '', linkedin: '', google: '', company_logo: '' },
    brand_name: '', brand_logo_url: '', brand_tagline: '', brand_color: '#1D4ED8',
    brand_whatsapp: '', brand_phone: '', brand_instagram: '', brand_website: '', brand_address: '',
    // One-time payment profile — auto-inherited by every deal you post
    payment_upi: '', payment_qr_url: '', payment_bank_name: '', payment_account_holder: '',
    payment_account_number: '', payment_ifsc: '', payment_branch: '', payment_instructions: '',
  });

  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.name || '',
        nickname: user.nickname || '',
        phone: user.phone || '',
        pincode: user.pincode || '',
        town: user.town || '',
        profile_picture: user.picture || '',
        bio: user.bio || '',
        social_links: { youtube: '', instagram: '', linkedin: '', google: '', company_logo: '', ...(user.social_links || {}) },
        brand_name: user.brand_name || '',
        brand_logo_url: user.brand_logo_url || '',
        brand_tagline: user.brand_tagline || '',
        brand_color: user.brand_color || '#1D4ED8',
        brand_whatsapp: user.brand_whatsapp || '',
        brand_phone: user.brand_phone || '',
        brand_instagram: user.brand_instagram || '',
        brand_website: user.brand_website || '',
        brand_address: user.brand_address || '',
        payment_upi: user.payment_upi || '',
        payment_qr_url: user.payment_qr_url || '',
        payment_bank_name: user.payment_bank_name || '',
        payment_account_holder: user.payment_account_holder || '',
        payment_account_number: user.payment_account_number || '',
        payment_ifsc: user.payment_ifsc || '',
        payment_branch: user.payment_branch || '',
        payment_instructions: user.payment_instructions || '',
      });
    }
  }, [user]);

  const onPick = () => fileRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const res = await uploadImage(file);
    if (res) setForm(f => ({ ...f, profile_picture: res.url }));
    e.target.value = '';
  };

  const submit = async () => {
    if (!form.full_name?.trim()) {
      toast.error('Full name is required');
      return;
    }
    if (!form.pincode?.trim() || !form.town?.trim()) {
      toast.error('Pincode and town are required');
      return;
    }
    const digits = (form.phone || '').replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error('Enter a valid 10-digit mobile number (needed for payments)');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch('/profile', form);
      setUser(data);
      toast.success('Profile saved');
      navigate('/chats');
    } catch (err) {
      toast.error('Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-black tracking-tighter display">Your profile</h1>
        <p className="text-sm text-[var(--bz-muted)] mt-1">Name, phone, pincode and town are required. Everything else is optional, but a complete profile makes your visiting card shine.</p>
      </div>

      <Card className="p-6 grid md:grid-cols-[160px_1fr] gap-6">
        <div className="flex flex-col items-center gap-3">
          {form.profile_picture ? (
            <ImageZoom src={form.profile_picture} testId="profile-picture-zoom">
              <Avatar className="h-32 w-32 ring-2 ring-[var(--bz-green-tint)] cursor-zoom-in">
                <AvatarImage src={form.profile_picture} />
                <AvatarFallback>{(form.nickname || form.full_name || '?').slice(0,2).toUpperCase()}</AvatarFallback>
              </Avatar>
            </ImageZoom>
          ) : (
            <Avatar className="h-32 w-32 ring-2 ring-[var(--bz-green-tint)]">
              <AvatarFallback>{(form.nickname || form.full_name || '?').slice(0,2).toUpperCase()}</AvatarFallback>
            </Avatar>
          )}
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid="profile-picture-input" />
          <Button variant="outline" size="sm" onClick={onPick} data-testid="upload-picture-button" className="gap-2">
            <Camera className="h-4 w-4" /> Change photo
          </Button>
          <p className="text-[10px] text-[var(--bz-muted)] text-center">Click avatar to view large</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Full name *</Label>
            <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} required data-testid="profile-fullname" />
          </div>
          <div>
            <Label>Nickname (optional)</Label>
            <Input value={form.nickname} onChange={e => setForm({ ...form, nickname: e.target.value })} data-testid="profile-nickname" />
          </div>
          <div>
            <Label>Phone *</Label>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required inputMode="numeric" pattern="[0-9]{10}" placeholder="10-digit mobile" data-testid="profile-phone" />
          </div>
          <div>
            <Label>Pincode *</Label>
            <Input value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value })} required data-testid="profile-pincode" />
          </div>
          <div className="sm:col-span-2">
            <Label>Town / City *</Label>
            <Input value={form.town} onChange={e => setForm({ ...form, town: e.target.value })} required data-testid="profile-town" />
          </div>
          <div className="sm:col-span-2">
            <Label>Bio</Label>
            <Textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} rows={3} data-testid="profile-bio" />
          </div>
        </div>
      </Card>

      <Card className="p-6 mt-4">
        <h3 className="text-lg font-bold display mb-3">Social & company</h3>
        <p className="text-xs text-[var(--bz-muted)] mb-4">These appear on your visiting card &amp; in QR-scan view (if you toggle them on).</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {['youtube', 'instagram', 'linkedin', 'google'].map(k => (
            <div key={k}>
              <Label className="capitalize">{k}</Label>
              <Input
                value={form.social_links[k] || ''}
                onChange={e => setForm({ ...form, social_links: { ...form.social_links, [k]: e.target.value } })}
                placeholder="@handle or URL"
                data-testid={`social-${k}`}
              />
            </div>
          ))}
        </div>
        <div className="mt-4 border-t pt-4">
          <Label className="text-sm font-semibold">Company logo</Label>
          <p className="text-xs text-[var(--bz-muted)] mb-2">Paste a URL or upload an image file — either works.</p>
          <div className="grid sm:grid-cols-[1fr_auto_140px] gap-2 items-start">
            <Input
              value={form.social_links.company_logo || ''}
              onChange={e => setForm({ ...form, social_links: { ...form.social_links, company_logo: e.target.value } })}
              placeholder="https://your-logo.png"
              data-testid="social-company_logo"
            />
            <div className="text-xs text-[var(--bz-muted)] self-center px-1">or</div>
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                const inp = document.createElement('input');
                inp.type = 'file'; inp.accept = 'image/*';
                inp.onchange = async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  const res = await uploadImage(file);
                  if (res) setForm(f => ({ ...f, social_links: { ...f.social_links, company_logo: res.url } }));
                };
                inp.click();
              }}
              className="gap-2"
              data-testid="upload-logo-button"
            >
              <UploadIcon className="h-4 w-4" /> Upload file
            </Button>
          </div>
          {form.social_links.company_logo && (
            <div className="mt-3">
              <ImageZoom src={form.social_links.company_logo} testId="company-logo-preview">
                <img src={form.social_links.company_logo} alt="logo" className="h-16 w-auto rounded-md border cursor-zoom-in" />
              </ImageZoom>
            </div>
          )}
        </div>
      </Card>

      {/* Brand profile — one-time setup, auto-applied to every deal you post */}
      <Card className="p-6 mt-4 border-[#1D4ED8]/30 bg-[#EAF1FF]/30" data-testid="brand-profile-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg font-bold display">Brand profile (for Deals)</h3>
            <p className="text-xs text-[var(--bz-muted)] mt-1">Set up your shop logo, brand name, contact & WhatsApp <b>once</b>. Every deal you post auto-uses these — no need to re-enter them each time you upload products.</p>
          </div>
          {form.brand_logo_url && <img src={form.brand_logo_url} alt="" className="h-14 w-14 rounded-lg border bg-white object-contain p-1" />}
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <Label>Brand / shop name</Label>
            <Input value={form.brand_name} onChange={e => setForm({ ...form, brand_name: e.target.value })} placeholder="e.g. Sunita Sweets" data-testid="brand-profile-name" />
          </div>
          <div>
            <Label>Tagline (optional)</Label>
            <Input value={form.brand_tagline} onChange={e => setForm({ ...form, brand_tagline: e.target.value })} placeholder="e.g. Hand-made daily" data-testid="brand-profile-tagline" />
          </div>
          <div className="sm:col-span-2">
            <Label>Brand logo</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input value={form.brand_logo_url} onChange={e => setForm({ ...form, brand_logo_url: e.target.value })} placeholder="Paste image URL" data-testid="brand-profile-logo-url" />
              <Button type="button" variant="outline" onClick={async () => {
                const inp = document.createElement('input');
                inp.type = 'file'; inp.accept = 'image/*';
                inp.onchange = async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  const res = await uploadImage(file);
                  if (res) setForm(f => ({ ...f, brand_logo_url: res.url }));
                };
                inp.click();
              }} className="gap-2 shrink-0" data-testid="brand-profile-logo-upload"><UploadIcon className="h-4 w-4" />Upload</Button>
            </div>
          </div>
          <div>
            <Label>Brand colour</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={form.brand_color} onChange={e => setForm({ ...form, brand_color: e.target.value })} className="h-10 w-12 rounded cursor-pointer" data-testid="brand-profile-color" />
              <Input value={form.brand_color} onChange={e => setForm({ ...form, brand_color: e.target.value })} placeholder="#1D4ED8" />
            </div>
          </div>
          <div>
            <Label>Shop contact phone</Label>
            <Input value={form.brand_phone} onChange={e => setForm({ ...form, brand_phone: e.target.value })} placeholder="+91 98765 43210" data-testid="brand-profile-phone" />
          </div>
          <div>
            <Label>WhatsApp number</Label>
            <Input value={form.brand_whatsapp} onChange={e => setForm({ ...form, brand_whatsapp: e.target.value })} placeholder="+91 98765 43210" data-testid="brand-profile-whatsapp" />
          </div>
          <div>
            <Label>Instagram handle</Label>
            <Input value={form.brand_instagram} onChange={e => setForm({ ...form, brand_instagram: e.target.value })} placeholder="@yourbrand" data-testid="brand-profile-instagram" />
          </div>
          <div className="sm:col-span-2">
            <Label>Brand website (optional)</Label>
            <Input value={form.brand_website} onChange={e => setForm({ ...form, brand_website: e.target.value })} placeholder="https://yourbrand.com" data-testid="brand-profile-website" />
          </div>
          <div className="sm:col-span-2">
            <Label>Shop address (optional)</Label>
            <Input value={form.brand_address} onChange={e => setForm({ ...form, brand_address: e.target.value })} placeholder="Shop No. 12, MG Road, Hyderabad" data-testid="brand-profile-address" />
          </div>
        </div>

        {user?.personal_id && (
          <div className="mt-4 text-xs text-[var(--bz-muted)]">
            Your live brand store URL: <a href={`/store/${user.personal_id}`} target="_blank" rel="noopener noreferrer" className="font-mono text-[#1E3A8A] underline">{`${window.location.origin}/store/${user.personal_id}`}</a>
          </div>
        )}
      </Card>

      {/* Payment Profile — one-time setup, auto-applied to every deal you post */}
      <Card className="p-6 mt-6" data-testid="payment-profile-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-[#1D4ED8] text-white grid place-items-center">₹</div>
          <div>
            <h3 className="text-lg font-bold display">Payment profile</h3>
            <p className="text-xs text-[var(--bz-muted)]">Set your UPI/QR/bank details once — every deal you post will auto-use these so customers can pay you directly.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>UPI ID</Label>
            <Input value={form.payment_upi} onChange={e => setForm({ ...form, payment_upi: e.target.value })} placeholder="yourname@okhdfcbank" data-testid="payment-upi" />
          </div>
          <div>
            <Label>UPI QR code image</Label>
            <input type="file" accept="image/*" onChange={async (e) => {
              const file = e.target.files?.[0]; if (!file) return;
              const r = await uploadImage(file);
              if (r) setForm(f => ({ ...f, payment_qr_url: r.url }));
              e.target.value = '';
            }} data-testid="payment-qr-input" />
            {form.payment_qr_url && <img src={form.payment_qr_url} alt="QR" className="h-24 mt-2 rounded border" />}
          </div>
          <div>
            <Label>Account holder name</Label>
            <Input value={form.payment_account_holder} onChange={e => setForm({ ...form, payment_account_holder: e.target.value })} data-testid="payment-holder" />
          </div>
          <div>
            <Label>Bank name</Label>
            <Input value={form.payment_bank_name} onChange={e => setForm({ ...form, payment_bank_name: e.target.value })} placeholder="HDFC Bank" data-testid="payment-bank" />
          </div>
          <div>
            <Label>Account number</Label>
            <Input value={form.payment_account_number} onChange={e => setForm({ ...form, payment_account_number: e.target.value })} data-testid="payment-account" />
          </div>
          <div>
            <Label>IFSC</Label>
            <Input value={form.payment_ifsc} onChange={e => setForm({ ...form, payment_ifsc: e.target.value })} placeholder="HDFC0001234" data-testid="payment-ifsc" />
          </div>
          <div>
            <Label>Branch (optional)</Label>
            <Input value={form.payment_branch} onChange={e => setForm({ ...form, payment_branch: e.target.value })} data-testid="payment-branch" />
          </div>
          <div className="sm:col-span-2">
            <Label>Payment instructions (optional)</Label>
            <Textarea rows={2} value={form.payment_instructions} onChange={e => setForm({ ...form, payment_instructions: e.target.value })} placeholder="e.g. Please send screenshot to my WhatsApp after paying" data-testid="payment-instructions" />
          </div>
        </div>
      </Card>


      <div className="mt-6 flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/chats')} data-testid="cancel-profile">Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-2" data-testid="save-profile-button">
          <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save Profile'}
        </Button>
      </div>
    </div>
  );
};

export default ProfileSetup;
